import { z } from 'zod';

import { TimestampSchema } from '../types/common.types';

/**
 * OmniDesk (E-OMNI, 2026-07-05) — ügyfélszolgálat-inbox BE↔FE wire single-source.
 *
 * Adverzáriális review-ratify (Zeno-V BE + Jarvis FE): a FE `safeParse`-ol EZ ellen
 * (JF-4 wire-gate, #719-minta), a BE ezt szolgálja ki. A route-válasz a `{ data }`-envelope
 * (JF-2 — a meglévő controller-konvenció; NEM `{ threads }`). Ez a wire-DTO NEM tükrözi
 * 1:1 a Prisma-modellt: RBAC-szűrt + denormalizált (customerEmail/mailboxAddress a listához).
 */

/** Támogatott inbound csatorna (a séma-enum tükre; WEBCHAT már él, email-only MVP-ben nem használt). */
export const ThreadChannelSchema = z.enum(['EMAIL', 'WEBCHAT', 'CONTACT_FORM']);
export type ThreadChannel = z.infer<typeof ThreadChannelSchema>;

/** Szál-állapot. */
export const ThreadStatusSchema = z.enum(['OPEN', 'PENDING', 'RESOLVED', 'SNOOZED']);
export type ThreadStatus = z.infer<typeof ThreadStatusSchema>;

/** Üzenet-irány (INBOUND=ügyfél, OUTBOUND=agent-válasz, SYSTEM=esemény). */
export const MessageDirectionSchema = z.enum(['INBOUND', 'OUTBOUND', 'SYSTEM']);
export type MessageDirection = z.infer<typeof MessageDirectionSchema>;

/** Kimenő-szinkron állapot (Dr. Quinn TRIZ optimistic-mirror). */
export const MessageSyncStatusSchema = z.enum(['PENDING_SYNC', 'SYNCED', 'FAILED']);
export type MessageSyncStatus = z.infer<typeof MessageSyncStatusSchema>;

/** SLA-állapot (BE-4: cron-számított + stored snapshot; a FE badge-forrása, 0 FE-számítás). */
export const SlaStateSchema = z.enum(['OK', 'DUE_SOON', 'BREACHED', 'ESCALATED']);
export type SlaState = z.infer<typeof SlaStateSchema>;

/** Csatolmány-referencia (S3-backed, D-3). */
export const OmniAttachmentSchema = z.object({
  name: z.string(),
  url: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  contentType: z.string(),
});
export type OmniAttachment = z.infer<typeof OmniAttachmentSchema>;

/**
 * ThreadSummary — a lista-sor wire-DTO (RBAC-szűrt). A `unread` a BE-oldali derive
 * (JF-1: lastInboundAt > lastReadAt), a `slaState` a stored snapshot — 0 FE-számítás.
 */
export const ThreadSummarySchema = z.object({
  id: z.string().cuid(),
  channel: ThreadChannelSchema,
  status: ThreadStatusSchema,
  subject: z.string().nullable(),
  customerName: z.string().nullable(),
  customerEmail: z.string().nullable(),
  mailboxAddress: z.string().nullable(),
  lastMessageAt: TimestampSchema,
  lastInboundAt: TimestampSchema.nullable(),
  assigneeUserId: z.string().nullable(),
  assigneeName: z.string().nullable(),
  slaState: SlaStateSchema,
  unread: z.boolean(),
});
export type ThreadSummary = z.infer<typeof ThreadSummarySchema>;

/** OmniMessage — egy szál-üzenet wire-DTO (az `OmniMessage` prefix a Prisma CustomerMessage-től elkülönít). */
export const OmniMessageSchema = z.object({
  id: z.string().cuid(),
  threadId: z.string().cuid(),
  direction: MessageDirectionSchema,
  content: z.string(),
  contentHtml: z.string().nullable(),
  authorUserId: z.string().nullable(),
  authorName: z.string().nullable(),
  syncStatus: MessageSyncStatusSchema,
  attachments: z.array(OmniAttachmentSchema),
  createdAt: TimestampSchema,
});
export type OmniMessage = z.infer<typeof OmniMessageSchema>;

/** PartnerContext — a thread-detail partner-panel; `null`, ha nincs linkelt partner (P-6 manuális link). */
export const PartnerContextSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    lastRentalSummary: z.string().nullable(),
    openBalance: z.number().nullable(),
    serviceLogHref: z.string().nullable(),
  })
  .nullable();
export type PartnerContext = z.infer<typeof PartnerContextSchema>;

/** ThreadDetail = ThreadSummary + kontakt-extra + üzenet-lista + partner-context. */
export const ThreadDetailSchema = ThreadSummarySchema.extend({
  customerPhone: z.string().nullable(),
  partnerId: z.string().nullable(),
  messages: z.array(OmniMessageSchema),
  partner: PartnerContextSchema,
});
export type ThreadDetail = z.infer<typeof ThreadDetailSchema>;

/** Lista-válasz payload (a route a `{ data: ... }`-envelope-ba csomagolja — JF-2). */
export const ThreadListPayloadSchema = z.object({
  data: z.array(ThreadSummarySchema),
  nextCursor: z.string().nullable().optional(),
});
export type ThreadListPayload = z.infer<typeof ThreadListPayloadSchema>;
